/**
 * The sign-in screen.
 *
 * The button is gated on MSAL's OWN interaction status and nothing else. It
 * used to be gated on the auth context's `loading`, which is the /me probe —
 * so an expired session (ticket #33) left the only escape hatch disabled for as
 * long as the doomed silent renewal took, with no explanation on screen.
 * `loginRedirect` throws for exactly one reason, `interaction_in_progress`, and
 * `inProgress` is the state that reports it; silent renewals never move it.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { InteractionStatus } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons'
import { faRightFromBracket } from '@fortawesome/free-solid-svg-icons'
import { useAuth } from './AuthProvider'
import { Logo } from '../../components/Logo'

export const LoginPage = () => {
    const { user, status, unauthorized, login, logout, retry } = useAuth()
    const { inProgress } = useMsal()
    const navigate = useNavigate()
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (user) navigate('/', { replace: true })
    }, [user, navigate])

    const onSignIn = async () => {
        setError(null)
        setSubmitting(true)
        try {
            await login()
        } catch {
            setError('Could not start sign-in. Try again.')
            setSubmitting(false)
        }
    }

    const busy = submitting || inProgress !== InteractionStatus.None

    return (
        <div className="surface-base relative flex min-h-[100dvh] w-full items-center justify-center px-6 py-12">
            <div className="flex w-full max-w-md flex-col items-center gap-8">
                <Logo className="h-20 md:h-24" />

                <div
                    className="border-base w-full rounded-xl border p-6 sm:p-7"
                    style={{
                        background: 'var(--surface-card)',
                        boxShadow: 'var(--shadow-panel)',
                    }}
                >
                    {unauthorized ? (
                        <div className="space-y-4">
                            <div>
                                <div className="text-strong text-base font-semibold tracking-tight">
                                    Account not authorized
                                </div>
                                <div className="text-weak text-sm">
                                    You are signed in with Microsoft, but this account is not set up
                                    in Tradeski yet. Ask an administrator to add you by email.
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost focusable w-full"
                                onClick={logout}
                            >
                                <FontAwesomeIcon
                                    icon={faRightFromBracket}
                                    className="h-3.5 w-3.5"
                                />
                                Sign out
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-5">
                            <div className="space-y-2">
                                <div className="eyebrow">Welcome back</div>
                                <h1 className="display text-2xl">Sign in to your Account</h1>
                            </div>

                            {/* Microsoft caps a single-page app's session at 24 hours. Say
                                so, rather than dropping the user on a bare login screen and
                                letting them wonder what they did wrong. */}
                            {status === 'expired' && (
                                <div
                                    className="rounded-md border px-3 py-2 text-sm"
                                    style={{
                                        borderColor: 'var(--border-base)',
                                        background: 'var(--surface-sunken)',
                                        color: 'var(--text-weak)',
                                    }}
                                >
                                    Your session expired. Sign in again to pick up where you left
                                    off.
                                </div>
                            )}

                            {status === 'unavailable' && (
                                <div className="space-y-2">
                                    <div className="field-error">
                                        Could not reach the sign-in service. Check your connection.
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-ghost focusable w-full"
                                        onClick={retry}
                                    >
                                        Retry
                                    </button>
                                </div>
                            )}

                            {error && <div className="field-error">{error}</div>}

                            <button
                                type="button"
                                className="btn btn-primary focusable w-full"
                                onClick={onSignIn}
                                disabled={busy}
                            >
                                <FontAwesomeIcon icon={faMicrosoft} className="h-4 w-4" />
                                {busy ? 'Redirecting…' : 'Continue with Microsoft'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="text-muted absolute bottom-4 left-1/2 -translate-x-1/2 text-xs">
                © {new Date().getFullYear()} Tradeski
            </div>
        </div>
    )
}
